import { useEffect, useState } from "react";
import { SlidersHorizontal, RotateCcw, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LoadCard from "@/components/LoadCard";
import PageHeader, { Empty } from "@/components/PageHeader";
import SavedRoutes from "@/components/SavedRoutes";
import CommodityOptions from "@/components/CommodityOptions";
import AreaSuggestions from "@/components/AreaSuggestions";
import FilterLocation from "@/components/FilterLocation";
import { t } from "@/lib/i18n";

const EMPTY = { loading_country: "", loading_region: "", loading_area: "", offloading_country: "", offloading_region: "", offloading_area: "", truck_type: "", commodity: "" };

export default function Marketplace() {
  const { meta, user } = useAuth();
  const [filters, setFilters] = useState(EMPTY);
  const [loads, setLoads] = useState(null);
  const [areas, setAreas] = useState({ loading: [], offloading: [] });
  const [showFilters, setShowFilters] = useState(false);
  const activeCount = Object.values(filters).filter(Boolean).length;

  useEffect(() => { api.get("/loads/areas").then((r) => setAreas(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      api.get("/loads", { params }).then((r) => setLoads(r.data)).catch(() => setLoads([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [filters]);

  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });

  return (
    <div>
      <PageHeader kicker={t("Live freight stream")} title={t("Load Board")} />
      {user.role !== "admin" && user.country && <p className="-mt-4 mb-5 text-sm text-slate-500" data-testid="country-scope-note">{t("Showing loads from shippers registered in {country}", { country: t(user.country) })}</p>}
      <div className="grid lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3">
          <button onClick={() => setShowFilters((v) => !v)} className="lg:hidden hf-btn-ghost w-full justify-between" data-testid="btn-toggle-filters">
            <span className="flex items-center gap-2"><SlidersHorizontal className="w-4 h-4" /> {t("Filters")} {activeCount > 0 && <span className="h-5 min-w-5 px-1.5 rounded-full bg-amber-500 text-slate-900 text-[11px] font-bold grid place-items-center" data-testid="active-filter-count">{activeCount}</span>}</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? "rotate-180" : ""}`} />
          </button>
          <div className={`hf-card p-5 lg:sticky lg:top-24 space-y-4 mt-2 lg:mt-0 ${showFilters ? "block" : "hidden lg:block"}`} data-testid="load-filters">
            <p className="hidden lg:flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-slate-500"><SlidersHorizontal className="w-3.5 h-3.5" /> {t("Filters")}</p>
            {["loading", "offloading"].map((k) => <FilterLocation key={k} kind={k} filters={filters} setFilters={setFilters} meta={meta} areas={areas} />)}
            <div>
              <label className="hf-label">{t("Truck type")}</label>
              <select value={filters.truck_type} onChange={set("truck_type")} className="hf-input" data-testid="select-filter-truck-type">
                <option value="">{t("All trucks")}</option>
                {meta.truck_types.map((tt) => <option key={tt} value={tt}>{t(tt)}</option>)}
              </select>
            </div>
            <div>
              <label className="hf-label">{t("Commodity")}</label>
              <select value={filters.commodity} onChange={set("commodity")} className="hf-input" data-testid="select-filter-commodity">
                <option value="">{t("All commodities")}</option>
                <CommodityOptions meta={meta} />
              </select>
            </div>
            <button onClick={() => setFilters(EMPTY)} className="hf-btn-ghost w-full" data-testid="btn-reset-filters"><RotateCcw className="w-4 h-4" /> {t("Reset")}</button>
          </div>
        </aside>
        <section className="lg:col-span-9 space-y-3 min-w-0">
          {user.role === "transporter" && <SavedRoutes filters={filters} onApply={(r) => setFilters(Object.fromEntries(Object.keys(EMPTY).map((k) => [k, r[k] || ""])))} />}
          <p className="font-mono text-xs text-slate-500" data-testid="load-count">{loads ? t("{n} open loads", { n: loads.length }).toUpperCase() : t("LOADING…")}</p>
          <AreaSuggestions filters={filters} loads={loads} onPick={(k, v) => setFilters({ ...filters, [k]: v })} />
          {loads?.length === 0 && <Empty title={t("No loads match")} testId="loads-empty">{t("Try widening the area or truck type filters.")}</Empty>}
          {loads?.map((l, i) => <LoadCard key={l.id} load={l} index={i} />)}
        </section>
      </div>
    </div>
  );
}
